import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Exchange } from './sessionStore'
import { useSessionStore } from './sessionStore'
import { useSettingsStore } from './settingsStore'

/** Bounded so localStorage can't grow without limit across sessions. */
const MAX_CHATS = 60

export interface StoredChat {
  id: string
  exchanges: Exchange[]
  createdAt: number
  updatedAt: number
  pinned?: boolean
}

export interface ChatsState {
  chats: StoredChat[]
  upsert: (input: { id: string; exchanges: Exchange[] }) => void
  remove: (id: string) => void
  clear: () => void
  togglePin: (id: string) => void
}

export const useChatsStore = create<ChatsState>()(
  persist(
    (set) => ({
      chats: [],

      upsert: ({ id, exchanges }) =>
        set((state) => {
          const existing = state.chats.find((chat) => chat.id === id)
          const next: StoredChat = {
            id,
            exchanges,
            createdAt: existing?.createdAt ?? Date.now(),
            updatedAt: Date.now(),
            pinned: existing?.pinned,
          }
          return {
            chats: [next, ...state.chats.filter((chat) => chat.id !== id)].slice(
              0,
              MAX_CHATS,
            ),
          }
        }),

      remove: (id) =>
        set((state) => ({ chats: state.chats.filter((chat) => chat.id !== id) })),

      clear: () => set({ chats: [] }),

      togglePin: (id) =>
        set((state) => ({
          chats: state.chats.map((chat) =>
            chat.id === id ? { ...chat, pinned: !chat.pinned } : chat,
          ),
        })),
    }),
    { name: 'qap.chats.v1', version: 1 },
  ),
)

/**
 * Mirrors the live thread into durable storage on every change, keyed by
 * conversation id. This is what makes the sidebar's chat list a real history
 * — clicking an entry restores these exchanges into the session — rather than
 * a log of query strings that gets re-submitted as a new message.
 */
useSessionStore.subscribe((state, prevState) => {
  if (state.exchanges === prevState.exchanges) return
  if (state.exchanges.length === 0) return
  if (!useSettingsStore.getState().rememberHistory) return

  useChatsStore.getState().upsert({ id: state.conversationId, exchanges: state.exchanges })
})
